import {
  add,
  addDays,
  addMonths,
  addYears,
  endOfDecade,
  format,
  formatISO,
  getMonth,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  isSameYear,
  startOfDecade,
  startOfMonth,
  startOfYear,
  subYears,
} from 'date-fns';
import { DayState, DayConfig, DaysAvailability, DayStatus } from '../../../../types/types';
import { h } from '@stencil/core';
import { month, localizedMonth, localizedDaysOfWeek } from '../../../../utils/utils';
import { Locale } from 'date-fns/locale/types';

export enum SwitchView {
  Month,
  Year,
  Decade,
}

export const makeDatesForMonth = ({
  month,
  isHoverActive,
  bookingDate,
  endDate,
  currentHoverDate,
  withDateRange,
  daysAvailability,
  onlyDropOffOnPickup,
  partiallyAvailableDays,
}: {
  month: Date;
  isHoverActive: boolean;
  bookingDate: Date;
  endDate: Date;
  currentHoverDate: Date;
  withDateRange: boolean;
  daysAvailability: DaysAvailability;
  onlyDropOffOnPickup: boolean;
  partiallyAvailableDays: boolean;
}): DayConfig[] => {
  let currentDate = startOfMonth(month);

  const allowPast = !daysAvailability;
  const today = new Date();
  const dates = [];

  while (getMonth(currentDate) == getMonth(startOfMonth(month))) {
    const isToday = isSameDay(currentDate, today);
    const isBeforeToday = isBefore(currentDate, today) && !isSameDay(currentDate, today);
    const isPickupDateToday = isSameDay(currentDate, bookingDate);
    const isDropOffDateToday = isSameDay(currentDate, endDate);

    //hover range
    const isDropHoverOffDate =
      isHoverActive && //
      isSameDay(currentDate, currentHoverDate);
    const isInHoverRange =
      isHoverActive && //
      !(isPickupDateToday && isDropHoverOffDate) &&
      ((isAfter(currentDate, bookingDate) && //
        isBefore(currentDate, currentHoverDate)) ||
        (isPickupDateToday && currentHoverDate && !endDate) ||
        isDropHoverOffDate) &&
      withDateRange;
    const inHoverRangeLeftRound = isHoverActive && isPickupDateToday && !isDropHoverOffDate;
    const inHoverRangeRightRound =
      !isBefore(currentHoverDate, bookingDate) &&
      isHoverActive && //
      isDropHoverOffDate &&
      !isPickupDateToday;

    const isAfterOrOnPickup =
      isAfter(currentDate, bookingDate) || //
      isPickupDateToday;
    const isInRange =
      ((isAfter(currentDate, bookingDate) && //
        isBefore(currentDate, endDate)) ||
        (isPickupDateToday && !isDropOffDateToday && endDate) ||
        (isDropOffDateToday && !isPickupDateToday)) &&
      withDateRange;
    const inRangeLeftRound = isPickupDateToday && !isDropOffDateToday;
    const inRangeRightRound =
      !isBefore(currentDate, bookingDate) && //
      isDropOffDateToday &&
      !isPickupDateToday;
    const isoCurrentDate = formatISO(currentDate, { representation: 'date' });

    let dayState: DayState;
    if (allowPast) {
      dayState = DayState.Selectable;
    } else {
      dayState = !!daysAvailability[isoCurrentDate] ? daysAvailability[isoCurrentDate].state : DayState.Blocked;
    }

    if (partiallyAvailableDays && dayState === DayState.Selectable && daysAvailability?.[isoCurrentDate]?.status === DayStatus.PartiallyBooked) {
      dayState = DayState.SelectablePartially;
    }

    const clickToReset: boolean =
      withDateRange && // All in range
      (!!(bookingDate && endDate) || // 1. Date selection is finished
        !!(bookingDate && dayState !== DayState.Selectable && bookingDate && dayState !== DayState.SelectablePartially) || // OR 2. On click on any not selectable day in hover range
        (isPickupDateToday && onlyDropOffOnPickup)); // OR 3. Clicking on pick up, when it is also treated as drop off

    const dayConfig: DayConfig = {
      date: currentDate,
      isToday: isToday,
      inPast: isBeforeToday,
      dayState: dayState,
      selected: isPickupDateToday || isDropOffDateToday,
      isPickupDate: isPickupDateToday,
      isDropOffDate: isDropOffDateToday,
      isInRange: isInRange,
      isDropHoverOffDate: isDropHoverOffDate,
      inRangeLeftRound: inRangeLeftRound,
      inRangeRightRound: inRangeRightRound,
      isInHoverRange: isInHoverRange,
      inHoverRangeLeftRound: inHoverRangeLeftRound,
      inHoverRangeRightRound: inHoverRangeRightRound,
      clickToReset: clickToReset,
      isAfterOrOnPickup: isAfterOrOnPickup,
    };

    dates.push(dayConfig);
    currentDate = addDays(currentDate, 1);
  }

  return dates;
};

export const renderDecadeView = ({
  selectedCalendarDate,
  changeCalendarDate,
  setSwitchView,
  allowPast,
}: {
  selectedCalendarDate: Date;
  allowPast: boolean;
  setSwitchView: (view: SwitchView) => void;
  changeCalendarDate: (date: Date, updateParent: boolean) => void;
}) => {
  const getYearsOfSelectedDecade = () => {
    const startOfCurrentDecade = startOfDecade(selectedCalendarDate);
    const startOfCurrentDecadeMinusOne = subYears(startOfCurrentDecade, 1);
    return Array.from({ length: 12 }, (_, i) => addYears(startOfCurrentDecadeMinusOne, i));
  };

  const setSelectedYear = (year: Date) => {
    const newDate = new Date(year.getFullYear(), selectedCalendarDate.getMonth(), 1);
    changeCalendarDate(newDate, false);
  };

  const yearIsBeforeStartOfCurrentYear = (year: Date) => {
    const startOfCurrentYear = startOfYear(new Date());
    return isBefore(year, startOfCurrentYear);
  };

  const years = getYearsOfSelectedDecade();

  return (
    <div class="decadeView" role="grid" aria-label="Select a year">
      {years.map(year => {
        const yearFormatted = format(year, 'yyyy');
        const label = isSameYear(selectedCalendarDate, year) ? `current year: ${yearFormatted}` : yearFormatted;
        return (
          <button
            onClick={() => {
              setSelectedYear(year);
              setSwitchView(SwitchView.Year);
            }}
            disabled={!allowPast && yearIsBeforeStartOfCurrentYear(year)}
            class={isSameYear(selectedCalendarDate, year) ? 'selected' : ''}
            aria-label={label}
            aria-current={isSameYear(selectedCalendarDate, year) ? 'true' : undefined}
          >
            {yearFormatted}
          </button>
        );
      })}
    </div>
  );
};

export const renderYearView = ({
  currentLocale,
  selectedCalendarDate,
  allowPast,
  changeCalendarDate,
  setSwitchView,
}: {
  currentLocale: Locale;
  selectedCalendarDate: Date;
  allowPast: boolean;
  setSwitchView: (view: SwitchView) => void;
  changeCalendarDate: (date: Date) => void;
}) => {
  const getMonthsOfSelectedYear = () => {
    const startOfCurrentYear = startOfYear(selectedCalendarDate);
    return Array.from({ length: 12 }, (_, i) => addMonths(startOfCurrentYear, i));
  };

  const setSelectedMonth = (month: Date) => {
    const newDate = new Date(selectedCalendarDate.getFullYear(), month.getMonth(), 1);
    changeCalendarDate(newDate);
  };

  const monthIsBeforeStartOfCurrentMonth = (month: Date) => {
    const startOfCurrentMonth = startOfMonth(new Date());
    return isBefore(month, startOfCurrentMonth);
  };

  const months = getMonthsOfSelectedYear();

  return (
    <div class="yearView" role="grid" aria-label="Select a month">
      {months.map(month => {
        const monthAndYear = format(month, 'MMMM yyyy');
        const label = isSameMonth(selectedCalendarDate, month) ? `current month: ${monthAndYear}` : monthAndYear;
        return (
          <button
            onClick={() => {
              setSelectedMonth(month);
              setSwitchView(SwitchView.Month);
            }}
            disabled={!allowPast && monthIsBeforeStartOfCurrentMonth(month)}
            class={isSameMonth(selectedCalendarDate, month) ? 'selected' : ''}
            aria-label={label}
            aria-current={isSameMonth(selectedCalendarDate, month) ? true : undefined}
          >
            {format(month, 'MMM', { locale: currentLocale })}
          </button>
        );
      })}
    </div>
  );
};

export const renderViewSwitch = ({
  switchView,
  selectedCalendarDate,
  setSwitchView,
  numberOfMonths,
  currentLocale,
}: {
  switchView: SwitchView;
  selectedCalendarDate: Date;
  setSwitchView: (view: SwitchView) => void;
  numberOfMonths: number;
  currentLocale: Locale;
}) => {
  if (switchView === SwitchView.Decade) {
    return (
      <div class="view-switch">
        <span>
          {startOfDecade(selectedCalendarDate).getFullYear()} - {endOfDecade(selectedCalendarDate).getFullYear()}
        </span>
      </div>
    );
  }

  if (switchView === SwitchView.Year) {
    return (
      <button class="view-switch" onClick={() => setSwitchView(SwitchView.Decade)}>
        <span>{selectedCalendarDate.getFullYear()}</span>
      </button>
    );
  }

  const switchClass = numberOfMonths === 1 ? 'viewSwitchItem' : 'viewSwitchItemMultiple';
  const startOfSelectedMonth = startOfMonth(selectedCalendarDate);

  return (
    <button class="view-switch" onClick={() => setSwitchView(SwitchView.Year)}>
      <time dateTime={month(startOfSelectedMonth)}>
        <div class={switchClass}>{localizedMonth(startOfSelectedMonth, currentLocale)}</div>

        {numberOfMonths === 2 && <div class="viewSwitchItemMultiple">{localizedMonth(add(startOfSelectedMonth, { months: 1 }), currentLocale)}</div>}
      </time>
    </button>
  );
};

export const renderWeekDays = (currentLocale, firstDayOfWeek = 1) => {
  let daysOfWeek = localizedDaysOfWeek(currentLocale);

  if (firstDayOfWeek > 0) {
    daysOfWeek = [...daysOfWeek.slice(firstDayOfWeek), ...daysOfWeek.slice(0, firstDayOfWeek)];
  }

  return (
    <div class="DaysOfWeek">
      {daysOfWeek.map(day => {
        return <div>{day}</div>;
      })}
    </div>
  );
};
